// VendorProductList.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  IconButton,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Stack,
  useMediaQuery,
  Switch,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  Grid,
  Chip,
  InputAdornment,
  Breadcrumbs,
  Link,
  Fade,
  Divider,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Tooltip from "@mui/material/Tooltip";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InventoryIcon from "@mui/icons-material/Inventory";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";

function MobileProductCard({ product, onEdit, onDelete, onToggleFeatured, isSelected, onSelect }) {
  const statusColors = {
    published: { bg: "rgba(46,125,50,0.1)", color: "#2e7d32" },
    unlisted: { bg: "rgba(237,108,2,0.1)", color: "#f57c00" },
    draft: { bg: "rgba(158,158,158,0.1)", color: "#616161" },
  };

  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 3,
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        border: isSelected ? '2px solid #ff9900' : '2px solid transparent',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        },
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Checkbox
          checked={isSelected}
          onChange={() => onSelect(product._id)}
          sx={{ p: 0, mt: 0.5 }}
        />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Tooltip title={product.title || ''} arrow placement="top">
              <Typography fontWeight={600} sx={{ fontSize: '1rem', lineHeight: 1.3, cursor: 'pointer', maxWidth: '80%' }}>
                {product.title && product.title.length > 75 ? product.title.substring(0, 75) + '...' : product.title}
              </Typography>
            </Tooltip>
            <Chip
              label={product.status}
              size="small"
              sx={{
                bgcolor: statusColors[product.status]?.bg || '#f5f5f5',
                color: statusColors[product.status]?.color || '#666',
                fontWeight: 500,
                fontSize: '0.7rem',
              }}
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {product.category?.name || 'Uncategorized'}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box>
              <Typography fontWeight={700} sx={{ fontSize: '1.1rem', color: '#ff9900' }}>
                ₹{Number(product.price || 0).toLocaleString("en-IN")}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">Stock:</Typography>
              <Typography
                fontWeight={600}
                sx={{
                  color: (product.stock || 0) === 0 ? '#d32f2f' :
                         (product.stock || 0) <= 10 ? '#f57c00' : '#2e7d32'
                }}
              >
                {product.stock ?? "-"}
              </Typography>
              {(product.stock || 0) <= 10 && (product.stock || 0) > 0 && (
                <WarningIcon sx={{ color: "#f57c00", fontSize: 16 }} />
              )}
              {(product.stock || 0) === 0 && (
                <ErrorIcon sx={{ color: "#d32f2f", fontSize: 16 }} />
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={product.isFeatured || false}
                  onChange={() => onToggleFeatured && onToggleFeatured(product._id, !product.isFeatured)}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#ff9900',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#ff9900',
                    },
                  }}
                />
              }
              label="Featured"
              labelPlacement="end"
              sx={{ m: 0, '& .MuiTypography-root': { fontSize: '0.8rem' } }}
            />

            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={onEdit}
                  sx={{ color: '#ff9900', bgcolor: 'rgba(255,153,0,0.1)', '&:hover': { bgcolor: 'rgba(255,153,0,0.2)' } }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={product.status === "published" ? "Unlist" : "Publish"}>
                <IconButton
                  size="small"
                  onClick={async () => {
                    await api.patch("/products/" + product._id + "/toggle-visibility");
                    window.location.reload();
                  }}
                  sx={{
                    color: product.status === "published" ? "#f57c00" : "#2e7d32",
                    bgcolor: product.status === "published" ? 'rgba(245,124,0,0.1)' : 'rgba(46,125,50,0.1)',
                    '&:hover': {
                      bgcolor: product.status === "published" ? 'rgba(245,124,0,0.2)' : 'rgba(46,125,50,0.2)',
                    }
                  }}
                >
                  {product.status === "published" ? <VisibilityOffIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={onDelete}
                  sx={{ color: '#d32f2f', bgcolor: 'rgba(211,47,47,0.1)', '&:hover': { bgcolor: 'rgba(211,47,47,0.2)' } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

export default function VendorProductList() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  // Table header filters
  const [stockFilter, setStockFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [featuredFilter, setFeaturedFilter] = useState("all");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);

  // Delete dialog
  const [deleteId, setDeleteId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Inventory management
  const [editingStockId, setEditingStockId] = useState(null);
  const [editingStockValue, setEditingStockValue] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkStockValue, setBulkStockValue] = useState("");
  const [inventorySummary, setInventorySummary] = useState({
    totalProducts: 0,
    totalStock: 0,
    lowStockCount: 0,
    outOfStockCount: 0,
  });

  const [searchParams] = useSearchParams();
  const initialType = searchParams.get("type") || "all";
  const filterParam = searchParams.get("filter");

  const [showFilters, setShowFilters] = useState(false);

  // Apply filter from URL parameter
  useEffect(() => {
    if (filterParam === "low+out") {
      setStockFilter("low+out");
    }
  }, [filterParam]);

  /* ======================
     FETCH VENDOR PRODUCTS
     ====================== */
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await api.get("/products?type=" + initialType + "&vendor=true");
        setProducts(res.data.products || []);
      } catch (err) {
        console.error("Fetch products failed", err);
      }
    };

    fetchProducts();
  }, [initialType]);

  /* ======================
     DYNAMIC CATEGORY LIST
     ====================== */
  const CATEGORY_LIST = [
    ...new Map(
      products
        .filter((p) => p.category)
        .map((p) => [p.category._id, p.category])
    ).values(),
  ];

  /* ======================
     FILTERING
     ====================== */
  const filtered = products.filter((p) => {
    const matchesSearch = p.title?.toLowerCase().includes(search.toLowerCase());

    const matchesCategory = categoryFilter ? p.category?._id === categoryFilter : true;

    const matchesStock = (() => {
      const stock = p.stock || 0;
      switch (stockFilter) {
        case "low": return stock > 0 && stock <= 10;
        case "out": return stock === 0;
        case "sufficient": return stock > 10;
        case "low+out": return (stock > 0 && stock <= 10) || stock === 0;
        case "all":
        default: return true;
      }
    })();

    const matchesStatus = statusFilter === "all" || p.status === statusFilter;

    const matchesFeatured = (() => {
      switch (featuredFilter) {
        case "featured": return p.isFeatured === true;
        case "not-featured": return p.isFeatured === false;
        case "all":
        default: return true;
      }
    })();

    return matchesSearch && matchesCategory && matchesStock && matchesStatus && matchesFeatured;
  });

  const isMobile = useMediaQuery("(max-width:768px)");

  /* ======================
     PAGINATION
     ====================== */
  const paginated = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* ======================
     TOGGLE FEATURED STATUS
     ====================== */
  const handleToggleFeatured = async (productId, newFeaturedStatus) => {
    try {
      await api.patch("/products/" + productId, {
        isFeatured: newFeaturedStatus
      });

      setProducts((prev) =>
        prev.map((p) =>
          p._id === productId
            ? { ...p, isFeatured: newFeaturedStatus, featuredAt: newFeaturedStatus ? new Date() : null }
            : p
        )
      );
    } catch (error) {
      console.error("Failed to update featured status:", error);
    }
  };

  /* ======================
     DELETE PRODUCT
     ====================== */
  const confirmDelete = async () => {
    try {
      await api.delete("/products/" + deleteId);
      setProducts((prev) => prev.filter((p) => p._id !== deleteId));
    } catch {
      alert("Failed to delete product");
    } finally {
      setDeleteOpen(false);
      setDeleteId(null);
    }
  };

  /* ======================
     INVENTORY MANAGEMENT
     ====================== */
  const startEditStock = (productId, currentStock) => {
    setEditingStockId(productId);
    setEditingStockValue(currentStock?.toString() || "");
  };

  const saveStockEdit = async () => {
    if (!editingStockId || editingStockValue === "") return;

    try {
      const stockNum = parseInt(editingStockValue, 10);
      if (isNaN(stockNum) || stockNum < 0) {
        alert("Please enter a valid non-negative number");
        return;
      }

      await api.patch("/products/" + editingStockId, { stock: stockNum });

      setProducts((prev) =>
        prev.map((p) =>
          p._id === editingStockId ? { ...p, stock: stockNum } : p
        )
      );

      setEditingStockId(null);
      setEditingStockValue("");
    } catch (error) {
      console.error("Failed to update stock:", error);
      alert("Failed to update stock");
    }
  };

  const cancelStockEdit = () => {
    setEditingStockId(null);
    setEditingStockValue("");
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const selectAllProducts = () => {
    if (selectedProducts.length === filtered.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filtered.map((p) => p._id));
    }
  };

  const handleBulkStockUpdate = async () => {
    if (!bulkStockValue || selectedProducts.length === 0) {
      alert("Please select products and enter a stock value");
      return;
    }

    const stockNum = parseInt(bulkStockValue, 10);
    if (isNaN(stockNum) || stockNum < 0) {
      alert("Please enter a valid non-negative number");
      return;
    }

    try {
      const updates = selectedProducts.map((productId) => ({
        productId,
        stock: stockNum
      }));

      await api.post("/products/bulk-stock-update", { updates });

      setProducts((prev) =>
        prev.map((p) =>
          selectedProducts.includes(p._id) ? { ...p, stock: stockNum } : p
        )
      );

      setBulkStockValue("");
      setSelectedProducts([]);
      alert(`Stock updated for ${selectedProducts.length} product(s)`);
    } catch (error) {
      console.error("Bulk stock update failed:", error);
      alert("Failed to update stock for selected products");
    }
  };

  /* ======================
     CALCULATE SUMMARY
     ====================== */
  useEffect(() => {
    const publishedProducts = products.filter(p => p.status === "published");
    const totalProducts = publishedProducts.length;
    const totalStock = publishedProducts.reduce((sum, p) => sum + (Number(p.stock) || 0), 0);
    const lowStockCount = publishedProducts.filter((p) => {
      const stock = Number(p.stock) || 0;
      return stock > 0 && stock <= 10;
    }).length;
    const outOfStockCount = publishedProducts.filter((p) => (Number(p.stock) || 0) === 0).length;

    setInventorySummary({ totalProducts, totalStock, lowStockCount, outOfStockCount });
  }, [products]);

  /* ======================
     FILTER ACTIONS
     ====================== */
  const applyLowStockFilter = () => {
    setStockFilter("low");
    setPage(0);
  };

  const applyOutOfStockFilter = () => {
    setStockFilter("out");
    setPage(0);
  };

  const resetAllFilters = () => {
    setSearch("");
    setCategoryFilter("");
    setStockFilter("all");
    setStatusFilter("all");
    setFeaturedFilter("all");
    setPage(0);
  };

  const isAnyFilterActive = () => {
    return (
      search !== "" ||
      categoryFilter !== "" ||
      stockFilter !== "all" ||
      statusFilter !== "all" ||
      featuredFilter !== "all"
    );
  };

  // Modern stat card style
  const StatCard = ({ icon: Icon, value, label, color, bgColor, onClick, hint }) => (
    <Card
      sx={{
        borderRadius: 3,
        bgcolor: bgColor,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        border: '1px solid',
        borderColor: onClick ? 'transparent' : 'rgba(0,0,0,0.05)',
        height: '100%',
        minHeight: 90,
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
        } : {},
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Icon sx={{ color, fontSize: 24 }} />
          </Box>
          <Typography variant="h4" fontWeight={700} sx={{ color: '#333' }}>
            {value}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
          {label}
        </Typography>
        {hint && (
          <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
            {hint}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', p: { xs: 2, sm: 3 } }}>
      {/* Breadcrumb */}
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="medium" />} aria-label="breadcrumb">
          <Link underline="hover" color="inherit" onClick={() => navigate("/vendor/dashboard")} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <HomeIcon sx={{ mr: 0.5 }} />
            <Typography variant="body2">Vendor Dashboard</Typography>
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            Products
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Header */}
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ color: '#333', mb: 0.5 }}>
            Inventory
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your product catalog
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/vendor/products/new")}
          sx={{
            bgcolor: '#ff9900',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1.2,
            boxShadow: '0 4px 14px rgba(255,153,0,0.3)',
            '&:hover': {
              bgcolor: '#e68a00',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(255,153,0,0.4)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          Add Product
        </Button>
      </Box>

      {/* Inventory Summary Cards */}
      <Grid container spacing={{ xs: 1.5, sm: 2 }} mb={1.5} alignItems="stretch">
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            icon={InventoryIcon}
            value={inventorySummary.totalProducts}
            label="Total Products"
            color="#1976d2"
            bgColor="linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)"
            hint="Published items"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            icon={CheckIcon}
            value={inventorySummary.totalStock.toLocaleString()}
            label="Total Stock"
            color="#2e7d32"
            bgColor="linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            icon={WarningIcon}
            value={inventorySummary.lowStockCount}
            label="Low Stock"
            color="#f57c00"
            bgColor="linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)"
            onClick={applyLowStockFilter}
            hint="Click to filter"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            icon={ErrorIcon}
            value={inventorySummary.outOfStockCount}
            label="Out of Stock"
            color="#d32f2f"
            bgColor="linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)"
            onClick={applyOutOfStockFilter}
            hint="Click to filter"
          />
        </Grid>
      </Grid>

      {/* Bulk Update Panel */}
      {/* <Fade in={selectedProducts.length > 0}>
        <Paper sx={{ mb: 1.5, p: 2, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #ff9900' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                bgcolor: 'rgba(255,153,0,0.1)',
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
              }}>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#ff9900' }}>
                  {selectedProducts.length} selected
                </Typography>
              </Box>
            </Box>
            <TextField
              size="small"
              placeholder="Stock value"
              value={bulkStockValue}
              onChange={(e) => setBulkStockValue(e.target.value)}
              sx={{ width: { xs: '100%', sm: 150 } }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<SaveIcon />}
              onClick={handleBulkStockUpdate}
              disabled={!bulkStockValue}
              sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
            >
              Update
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setSelectedProducts([])}
              sx={{ borderColor: '#ddd', color: '#666' }}
            >
              Clear
            </Button>
          </Stack>
        </Paper>
      </Fade> */}

      {/* Search & Filter Bar */}
      <Paper sx={{ mb: 1.5, p: 2, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        <Stack spacing={2}>
          {/* Main Search Row */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: 1 }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#999' }} />
                  </InputAdornment>
                ),
              }}
            />
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              displayEmpty
              size="small"
              sx={{ minWidth: { xs: '100%', sm: 180 } }}
            >
              <MenuItem value="">All Categories</MenuItem>
              {CATEGORY_LIST.map((cat) => (
                <MenuItem key={cat._id} value={cat._id}>{cat.name}</MenuItem>
              ))}
            </Select>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{
                borderColor: showFilters ? '#ff9900' : '#ddd',
                color: showFilters ? '#ff9900' : '#666',
              }}
            >
              Filters {isAnyFilterActive() && `(${['search', 'category', 'stock', 'status', 'featured'].filter(k => {
                if (k === 'search' && search) return true;
                if (k === 'category' && categoryFilter) return true;
                if (k === 'stock' && stockFilter !== 'all') return true;
                if (k === 'status' && statusFilter !== 'all') return true;
                if (k === 'featured' && featuredFilter !== 'all') return true;
                return false;
              }).length})`}
            </Button>
            {isAnyFilterActive() && (
              <Button
                variant="text"
                startIcon={<RefreshIcon />}
                onClick={resetAllFilters}
                sx={{ color: '#666' }}
              >
                Reset
              </Button>
            )}
          </Stack>

          {/* Extended Filters */}
          <Fade in={showFilters}>
            <Box>
              <Divider sx={{ mb: 2 }} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Select
                  value={stockFilter}
                  onChange={(e) => setStockFilter(e.target.value)}
                  displayEmpty
                  size="small"
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="all">All Stock</MenuItem>
                  <MenuItem value="low">Low (≤10)</MenuItem>
                  <MenuItem value="out">Out of Stock</MenuItem>
                  <MenuItem value="sufficient">Sufficient (&gt;10)</MenuItem>
                </Select>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  displayEmpty
                  size="small"
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="unlisted">Unlisted</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                </Select>
                <Select
                  value={featuredFilter}
                  onChange={(e) => setFeaturedFilter(e.target.value)}
                  displayEmpty
                  size="small"
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="all">All Featured</MenuItem>
                  <MenuItem value="featured">Featured</MenuItem>
                  <MenuItem value="not-featured">Not Featured</MenuItem>
                </Select>
              </Stack>
            </Box>
          </Fade>
        </Stack>
      </Paper>

      {/* Selection Bar */}
      {selectedProducts.length > 0 && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(255,153,0,0.1)', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" fontWeight={500}>
            {selectedProducts.length} of {filtered.length} selected
          </Typography>
          <Button size="small" variant="text" onClick={selectAllProducts} sx={{ color: '#ff9900' }}>
            {selectedProducts.length === filtered.length ? 'Deselect All' : 'Select All'}
          </Button>
        </Box>
      )}

      {/* Products Grid / Table */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', p: 0 }}>
        {isMobile ? (
          // Mobile: Card View
          <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
              {paginated.map((p) => (
                <MobileProductCard
                  key={p._id}
                  product={p}
                  onView={() => navigate("/vendor/product/" + p._id)}
                  onEdit={() => navigate("/vendor/edit-product/" + p._id)}
                  onDelete={() => { setDeleteId(p._id); setDeleteOpen(true); }}
                  onToggleFeatured={handleToggleFeatured}
                  isSelected={selectedProducts.includes(p._id)}
                  onSelect={toggleProductSelection}
                />
              ))}
              {filtered.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <InventoryIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">No products found</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {search || categoryFilter || stockFilter !== 'all' || statusFilter !== 'all' || featuredFilter !== 'all'
                      ? 'Try adjusting your filters'
                      : 'Add your first product to get started'}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        ) : (
          // Desktop: Modern Table
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 900 }}>
              {/* Table Header */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1.5,
                bgcolor: '#fafafa',
                borderBottom: '1px solid #eee',
                fontWeight: 600,
              }}>
                <Box sx={{ width: 40, flexShrink: 0 }}>
                  <Checkbox
                    indeterminate={selectedProducts.length > 0 && selectedProducts.length < filtered.length}
                    checked={filtered.length > 0 && selectedProducts.length === filtered.length}
                    onChange={selectAllProducts}
                    size="small"
                  />
                </Box>
                <Box sx={{ flex: 5, minWidth: 0 }}><Typography fontWeight={600} sx={{ fontSize: '0.85rem' }}>Product</Typography></Box>
                <Box sx={{ flex: 1, minWidth: 80 }}><Typography fontWeight={600} sx={{ fontSize: '0.85rem' }}>Category</Typography></Box>
                <Box sx={{ flex: 1, minWidth: 60, textAlign: 'center' }}><Typography fontWeight={600} sx={{ fontSize: '0.85rem' }}>Featured</Typography></Box>
                <Box sx={{ flex: 1, minWidth: 60, textAlign: 'right', pr: 5 }}><Typography fontWeight={600} sx={{ fontSize: '0.85rem' }}>Price</Typography></Box>
                <Box sx={{ flex: 1, minWidth: 60, textAlign: 'center' }}><Typography fontWeight={600} sx={{ fontSize: '0.85rem' }}>Stock</Typography></Box>
                <Box sx={{ flex: 1, minWidth: 60, textAlign: 'center' }}><Typography fontWeight={600} sx={{ fontSize: '0.85rem' }}>Status</Typography></Box>
                <Box sx={{ flex: 1, minWidth: 80, textAlign: 'right' }}><Typography fontWeight={600} sx={{ fontSize: '0.85rem' }}>Actions</Typography></Box>
              </Box>

              {/* Table Body */}
              {paginated.map((p) => {
                const statusColors = {
                  published: { bg: '#e8f5e9', color: '#2e7d32' },
                  unlisted: { bg: '#fff3e0', color: '#f57c00' },
                  draft: { bg: '#f5f5f5', color: '#616161' },
                };
                return (
                  <Box
                    key={p._id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 2,
                      py: 2,
                      borderBottom: '1px solid #f5f5f5',
                      transition: 'all 0.2s ease',
                      '&:hover': { bgcolor: 'rgba(255,153,0,0.03)' },
                    }}
                  >
                    <Box sx={{ width: 32, flexShrink: 0 }}>
                      <Checkbox
                        checked={selectedProducts.includes(p._id)}
                        onChange={() => toggleProductSelection(p._id)}
                        size="small"
                      />
                    </Box>
                    <Box sx={{ flex: 5, minWidth: 0, overflow: 'hidden' }}>
                      <Tooltip title={p.title || ''} arrow placement="top">
                        <Typography fontWeight={500} sx={{ fontSize: '0.9rem', cursor: 'pointer' }}>
                          {p.title && p.title.length > 75 ? p.title.substring(0, 75) + '...' : p.title}
                        </Typography>
                      </Tooltip>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 80, overflow: 'hidden' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.category?.name || '-'}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 60, textAlign: 'center' }}>
                      <Switch
                        checked={p.isFeatured || false}
                        onChange={() => handleToggleFeatured(p._id, !p.isFeatured)}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#ff9900' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#ff9900' },
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 60, textAlign: 'right', pr: 5 }}>
                      <Typography fontWeight={600} sx={{ color: '#ff9900', fontSize: '0.85rem' }}>₹{Number(p.price || 0).toLocaleString("en-IN")}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 60,textAlign: 'center' }}>
                      {editingStockId === p._id ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TextField
                            size="small"
                            value={editingStockValue}
                            onChange={(e) => setEditingStockValue(e.target.value)}
                            sx={{ width: 80 }}
                            autoFocus
                          />
                          <IconButton size="small" sx={{ color: '#2e7d32' }} onClick={saveStockEdit}><CheckIcon fontSize="small" /></IconButton>
                          <IconButton size="small" sx={{ color: '#d32f2f' }} onClick={cancelStockEdit}><CloseIcon fontSize="small" /></IconButton>
                        </Stack>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                          <Typography
                            fontWeight={600}
                            sx={{
                              color: (p.stock || 0) === 0 ? '#d32f2f' : (p.stock || 0) <= 10 ? '#f57c00' : '#333',
                              textAlign: 'center',
                            }}
                          >
                            {p.stock ?? "-"}
                          </Typography>
                          {(p.stock || 0) <= 10 && (p.stock || 0) > 0 && <WarningIcon sx={{ color: "#f57c00", fontSize: 16 }} />}
                          {(p.stock || 0) === 0 && <ErrorIcon sx={{ color: "#d32f2f", fontSize: 16 }} />}
                          <IconButton size="small" onClick={() => startEditStock(p._id, p.stock)} sx={{ color: '#999', ml: 0.5 }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 60, textAlign: 'center' }}>
                      <Chip
                        label={p.status}
                        size="small"
                        sx={{
                          bgcolor: statusColors[p.status]?.bg || '#f5f5f5',
                          color: statusColors[p.status]?.color || '#666',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 80, display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => navigate("/vendor/edit-product/" + p._id)}
                          sx={{ color: '#ff9900', '&:hover': { bgcolor: 'rgba(255,153,0,0.1)' } }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={p.status === "published" ? "Unlist" : "Publish"}>
                        <IconButton
                          size="small"
                          onClick={async () => {
                            await api.patch("/products/" + p._id + "/toggle-visibility");
                            setProducts((prev) =>
                              prev.map((prod) =>
                                prod._id === p._id
                                  ? { ...prod, status: prod.status === "published" ? "unlisted" : "published" }
                                  : prod
                              )
                            );
                          }}
                          sx={{
                            color: p.status === "published" ? "#f57c00" : "#2e7d32",
                            '&:hover': {
                              bgcolor: p.status === "published" ? 'rgba(245,124,0,0.1)' : 'rgba(46,125,50,0.1)',
                            }
                          }}
                        >
                          {p.status === "published" ? <VisibilityOffIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => { setDeleteId(p._id); setDeleteOpen(true); }}
                          sx={{ color: '#d32f2f', '&:hover': { bgcolor: 'rgba(211,47,47,0.1)' } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })}

              {filtered.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <InventoryIcon sx={{ fontSize: 64, color: '#ddd', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">No products found</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {search || categoryFilter || stockFilter !== 'all' || statusFilter !== 'all' || featuredFilter !== 'all'
                      ? 'Try adjusting your filters or search terms'
                      : 'Add your first product to get started'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Pagination */}
        <Box sx={{ px: 2, py: 2, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {paginated.length} of {filtered.length} products
          </Typography>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[8, 16, 32]}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.85rem' } }}
          />
        </Box>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <Box sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              bgcolor: 'rgba(211,47,47,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <DeleteIcon sx={{ color: '#d32f2f', fontSize: 30 }} />
            </Box>
          </Box>
          <Typography variant="h6" fontWeight={600}>Delete Product?</Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. The product will be permanently removed from your inventory.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 1 }}>
          <Button
            onClick={() => setDeleteOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2, borderColor: '#ddd', color: '#666' }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            sx={{ borderRadius: 2 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}